import { jourParis } from './reactivite.js';
/**
 * PRÉ-FACTURATION ET IMPUTATION EKIVOX (DEC-015, US-046, US-048, US-078).
 *
 * Q13 (tranchée le 01/10/2026, DEC-019) : une période de facturation est le
 * MOIS CALENDAIRE ; une correction après envoi devient une régularisation de
 * la période suivante. Tout ce qui dépend de cette règle passe par
 * `periodeDe` : la changer, c'est changer ce seul endroit.
 */
/** « AAAA-MM » : la période d'un jour « AAAA-MM-JJ ». */
export function periodeDe(jour) {
    return jour.slice(0, 7);
}
/** Le mois précédent, par défaut de l'aperçu (US-046). */
export function periodePrecedente(jour) {
    const [a, m] = jour.split('-').map(Number);
    return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}
/** Premier et dernier jour d'une période « AAAA-MM ». */
export function bornesPeriode(periode) {
    const [a, m] = periode.split('-').map(Number);
    const dernier = new Date(Date.UTC(a, m, 0)).getUTCDate();
    return { du: `${periode}-01`, au: `${periode}-${String(dernier).padStart(2, '0')}` };
}
export const PERIODE_LABEL = (periode) => new Date(`${periode}-15T12:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
export const MAX_DESTINATAIRES_RELEVE = 5;
/** Refus d'un envoi de relevé : dire ce qui bloque et quoi corriger (règle 9). */
export function refusEnvoiReleve(client, destinataires) {
    if (!destinataires.length) {
        // Le contact de facturation n'existe pas dans `core` (tenu par Ekivox) : on
        // ne peut donc pas renvoyer vers la fiche client, seulement vers la saisie.
        return `${client} n'a pas d'adresse de facturation connue. Saisissez au moins une adresse de destination.`;
    }
    if (destinataires.length > MAX_DESTINATAIRES_RELEVE) {
        return `${MAX_DESTINATAIRES_RELEVE} adresses au plus : retirez-en ${destinataires.length - MAX_DESTINATAIRES_RELEVE}.`;
    }
    return null;
}
/**
 * LES DÉPÔTS INQOM SE FACTURENT À ALTONEO CONSEIL (DEC-040).
 *
 * Le temps qu'IT Solutions passe sur ces articles du catalogue EC_RECURRENT
 * reste celui du client (feuille de temps, fiche, indicateurs), mais se
 * facture à l'ALTONEO CONSEIL du site rattaché du client dans Ekivox. Il ne
 * figure dans aucun relevé : seul le fichier Ekivox le porte.
 *
 * Le catalogue compte : les mêmes codes DIGT0102 et DIGT0104 désignent, dans
 * EC_PONCTUEL, un audit de facturation électronique et un diagnostic IA,
 * facturés au client comme le reste.
 */
export const ARTICLES_FACTURES_ALTONEO_CONSEIL = [
    { catalogue: 'EC_RECURRENT', code: 'DIGT0102' }, // Forfait dépôt Inqom (SCI, LMNP, Holding)
    { catalogue: 'EC_RECURRENT', code: 'DIGT0104' }, // Forfait dépôt Inqom autres organisations
];
/**
 * L'ALTONEO CONSEIL de chaque site Ekivox (`codeSiteRattachement`) : ses
 * dossiers Ekivox, relevés le 14/10/2026. Ekivox ne relie pas un site à son
 * entité : la correspondance est tenue ici, et nulle part ailleurs.
 */
export const ALTONEO_CONSEIL_PAR_SITE = {
    '49ANG': { numeroDossier: '014742', nom: 'ALTONEO CONSEIL ANGERS' },
    '53CHA': { numeroDossier: '014743', nom: 'ALTONEO CONSEIL CHANGE' },
    '53CHG': { numeroDossier: '014744', nom: 'ALTONEO CONSEIL CHATEAU GONTIER' },
    '72MAN': { numeroDossier: '014745', nom: 'ALTONEO CONSEIL LE MANS' },
    '35CES': { numeroDossier: '014746', nom: 'ALTONEO CONSEIL CESSON' },
    '49POU': { numeroDossier: '014747', nom: 'ALTONEO CONSEIL OMBREE' },
    '49CHE': { numeroDossier: '014748', nom: 'ALTONEO CONSEIL CHEMILLE' },
};
export function facturation(p) {
    const articles = ARTICLES_FACTURES_ALTONEO_CONSEIL.filter((a) => a.code === p.code);
    if (!p.code || !articles.length)
        return { sorte: 'client' };
    // Sans tâche, rien ne dit si c'est le dépôt Inqom ou l'audit du même code :
    // on ne devine pas à qui facturer.
    if (!p.catalogues.length)
        return { sorte: 'catalogue_inconnu', code: p.code };
    if (!articles.some((a) => p.catalogues.includes(a.catalogue)))
        return { sorte: 'client' };
    const entite = p.site ? ALTONEO_CONSEIL_PAR_SITE[p.site] : undefined;
    return entite ? { sorte: 'altoneo', ...entite } : { sorte: 'site_inconnu', code: p.code, site: p.site };
}
/** Ce que dit une `Facturation` qui empêche la ligne de partir ; null si elle peut partir. */
export function manqueFacturation(f, client) {
    if (f.sorte === 'site_inconnu') {
        return (`${f.code} se facture à l’ALTONEO CONSEIL du site de ${client}, mais ` +
            (f.site ? `le site « ${f.site} » ne correspond à aucune entité ALTONEO CONSEIL connue de la borne.` : 'Ekivox ne lui connaît aucun site rattaché.') +
            ' Corrigez le site du dossier dans Ekivox, puis relancez l’export.');
    }
    if (f.sorte === 'catalogue_inconnu') {
        return `Impossible de savoir si ${f.code} est le dépôt Inqom (EC_RECURRENT), facturé à ALTONEO CONSEIL, ou l’article du même code d’un autre catalogue : Ekivox ne lui connaît aucune tâche. Vérifiez la prestation dans Ekivox.`;
    }
    return null;
}
/**
 * Ce qui manque à une ligne pour partir vers Ekivox (règle 9 : dire quoi corriger).
 *
 * DEC-053 : la ligne porte une CLÉ — code dossier + code prestation — et la
 * mission se cherche à l'export (`prestationDeLaCle`). Sans mission trouvée,
 * Ekivox ne saurait où imputer : la ligne ne part pas. Une ligne d'avant la
 * clé, dont le miroir ne connaissait plus la prestation, se dit à part.
 */
export function manquesImputation(l) {
    const m = [];
    if (!l.numeroDossier)
        m.push(`${l.client} n’a pas de code dossier : il manque dans Ekivox.`);
    if (!l.idMission) {
        const [a, mo, j] = l.jour.split('-');
        if (l.code && l.numeroDossier) {
            m.push(`Aucune mission du dossier ${l.numeroDossier} ne porte la prestation ${l.code} le ${j}/${mo}/${a} dans Ekivox : ` +
                'faites créer ou prolonger la mission dans Ekivox, ou rouvrez l’intervention et choisissez une autre prestation.');
        }
        else if (!l.code) {
            m.push(l.ancienne
                ? 'Prestation d’origine introuvable dans Ekivox (retirée avant le passage à la clé dossier + code) : rouvrez l’intervention et choisissez une prestation.'
                : 'Aucune prestation Ekivox : rouvrez l’intervention et choisissez-en une.');
        }
    }
    return m;
}
/* ------------------------------------------------------------------ */
/* Export du mois : les lignes, et le temps resté hors                 */
/* ------------------------------------------------------------------ */
/**
 * L'ÉTAT d'une ligne de pré-facturation, en clair : ce que la colonne « État »
 * de l'écran montre en badge. Écrit une fois pour l'écran, l'export de l'API
 * et celui de la démo.
 */
export function etatLigneImputation(l) {
    if (l.regularisation) {
        return { etat: l.regularisation.sens === 'annule' ? 'Contre-passation' : 'Régularisation', detail: [l.regularisation.motif, ...l.manques].join(' ; ') };
    }
    if (l.exporteLe)
        return { etat: 'Exportée', detail: `Le ${l.exporteLe.slice(0, 10).split('-').reverse().join('/')}` };
    if (l.manques.length)
        return { etat: 'À compléter', detail: l.manques.join(' ; ') };
    return { etat: 'Prête', detail: '' };
}
/**
 * POURQUOI une intervention du mois n'est pas facturée — les seules raisons que
 * la borne connaît, dans l'ordre où elles l'écartent des lignes. Garantie ou
 * forfait ne sont pas tenus par la borne : elle ne les devine pas.
 */
export function motifNonFacturee(i) {
    if (i.sansClient)
        return 'Temps interne : sans client, il n’est pas facturé.';
    if (i.entiteITSolutions)
        return 'IT Solutions ne se facture pas à elle-même (DEC-033).';
    if (i.minutes <= 0)
        return 'Aucun temps facturable saisi.';
    if (i.enAttenteMission)
        return 'Mission demandée : la ligne partira une fois la mission créée et rattachée (DEC-043).';
    return 'Sans mission : rattachez une prestation pour qu’elle parte vers Ekivox.';
}
const commun = (r) => (r.facturee ? r.ligne : r.intervention);
const nonFacturable = (r) => (r.facturee ? r.heuresNonFacturables : r.intervention.heuresNonFacturables);
/** Une valeur des seules lignes facturées : vide pour une intervention restée hors. */
const ligneSeule = (r, f) => (r.facturee ? f(r.ligne) : '');
/**
 * L'EXPORT DU MOIS, dans l'ordre : les interventions du mois, facturées ou non,
 * par client puis par date — le bilan se lit client par client —, puis les
 * régularisations, qui corrigent un autre mois. `heuresNonFacturables` : celles
 * de chaque intervention des lignes, par identifiant ; une régularisation n'en
 * a pas (sa photo ne porte que le temps facturé).
 */
export function lignesExportMois(a, nonFacturees, heuresNonFacturables) {
    const mois = [
        ...a.lignes.map((ligne) => ({ facturee: true, ligne, heuresNonFacturables: heuresNonFacturables[ligne.interventionId] ?? 0 })),
        ...nonFacturees.map((intervention) => ({ facturee: false, intervention })),
    ];
    mois.sort((x, y) => commun(x).client.localeCompare(commun(y).client, 'fr') || commun(x).date.localeCompare(commun(y).date));
    return [...mois, ...a.regularisations.map((ligne) => ({ facturee: true, ligne, heuresNonFacturables: null }))];
}
/**
 * LES COLONNES DE L'EXPORT DU MOIS (pré-facturation) : celles du tableau, plus
 * ce que l'écran ne montre qu'en petit (mission, facturé à, détail de l'état),
 * et, pour le bilan, « Facturée » et son « Motif ». Le fichier sert à pointer
 * et à trier ; ce n'est PAS le fichier d'import Ekivox, qui garde son format
 * (« Vers Ekivox »).
 */
export const COLONNES_EXPORT_LIGNES = [
    { titre: 'Client', largeur: 32, valeur: (r) => ({ texte: commun(r).client }) },
    { titre: 'N° dossier', largeur: 12, valeur: (r) => ({ texte: commun(r).numeroDossier }) },
    { titre: 'Facturée', largeur: 9, valeur: (r) => ({ texte: r.facturee ? 'Oui' : 'Non' }) },
    { titre: 'Motif', largeur: 50, valeur: (r) => ({ texte: r.facturee ? '' : r.intervention.motif }) },
    { titre: 'Facturé à', largeur: 30, valeur: (r) => ({ texte: ligneSeule(r, (l) => (l.factureA ? `${l.factureA.nom} (${l.factureA.numeroDossier})` : '')) }) },
    { titre: 'Code prestation', largeur: 14, valeur: (r) => ({ texte: ligneSeule(r, (l) => l.codePrestation ?? '') }) },
    { titre: 'Prestation', largeur: 32, valeur: (r) => ({ texte: ligneSeule(r, (l) => l.libellePrestation ?? '') }) },
    { titre: 'Mission', largeur: 14, valeur: (r) => ({ texte: ligneSeule(r, (l) => l.idMission) }) },
    { titre: 'Date', largeur: 11, valeur: (r) => ({ jour: jourParis(new Date(commun(r).date)) }) },
    { titre: 'Intervenant', largeur: 22, valeur: (r) => ({ texte: commun(r).intervenant ?? '' }) },
    { titre: 'Type', largeur: 22, valeur: (r) => ({ texte: commun(r).typeIntervention ?? '' }) },
    { titre: 'Heures', largeur: 8, valeur: (r) => ({ nombre: commun(r).dureeHeures }) },
    {
        titre: 'Non facturable (h)',
        largeur: 10,
        valeur: (r) => {
            const h = nonFacturable(r);
            return h === null ? { texte: '' } : { nombre: h };
        },
    },
    { titre: 'Commentaire', largeur: 60, valeur: (r) => ({ texte: commun(r).commentaire }) },
    { titre: 'État', largeur: 16, valeur: (r) => ({ texte: ligneSeule(r, (l) => etatLigneImputation(l).etat) }) },
    { titre: 'Détail de l’état', largeur: 60, valeur: (r) => ({ texte: ligneSeule(r, (l) => etatLigneImputation(l).detail) }) },
];
const arrondi = (h) => Math.round(h * 100) / 100;
/**
 * LES TOTAUX en pied de l'export, sous une ligne vide (un tri ne les mêle pas
 * aux lignes) : le TOTAL FACTURÉ — les lignes, régularisations comprises,
 * celui qu'on exportait déjà —, puis le TOTAL DU MOIS : toutes ses
 * interventions, facturées ou non, et leur temps non facturable, sans les
 * régularisations, qui corrigent un autre mois. Séparés : l'un ne se lit pas
 * dans l'autre.
 */
export function totauxExportMois(lignes) {
    const mois = lignes.filter((r) => !(r.facturee && r.ligne.regularisation));
    const somme = (rs, f) => arrondi(rs.reduce((s, r) => s + f(r), 0));
    const rangee = (libelle, valeurs) => COLONNES_EXPORT_LIGNES.map((c, n) => (n === 0 ? { texte: libelle } : c.titre in valeurs ? { nombre: valeurs[c.titre] } : { texte: '' }));
    return [
        COLONNES_EXPORT_LIGNES.map(() => ({ texte: '' })),
        rangee('Total facturé', { Heures: somme(lignes.filter((r) => r.facturee), (r) => commun(r).dureeHeures) }),
        rangee('Total du mois (facturé ou non, hors régularisations)', {
            Heures: somme(mois, (r) => commun(r).dureeHeures),
            'Non facturable (h)': somme(mois, (r) => nonFacturable(r) ?? 0),
        }),
    ];
}
/** La feuille sans son en-tête : les lignes, puis les totaux. L'API et la démo écrivent la même. */
export function feuilleExportMois(lignes) {
    return [...lignes.map((r) => COLONNES_EXPORT_LIGNES.map((c) => c.valeur(r))), ...totauxExportMois(lignes)];
}
//# sourceMappingURL=prefacturation.js.map